import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { REWARD_APP_PREVIEW_EXPIRY_DISPLAY_FORMAT } from '@spotlight/shared/reward-app-preview/reward-app-preview.constants';
import { SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { EngageConstants } from '../../engage/engage.constants';
import { DiscoverConstants } from '../../discover/discover.constants';
import { CampaignType } from '../../models/engage/campaign-details.model';
import { AppConstants } from '../../app.constants';

@Component({
  selector: 'template-preview',
  templateUrl: './template-preview.component.html',
  styleUrls: ['./template-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class TemplatePreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  public readonly operatorPlaceholderImageUrl = AppConstants.operatorPlaceholderImageUrl;
  @ViewChildren('previewShell') private readonly previewShells!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('previewStage') private readonly previewStages!: QueryList<ElementRef<HTMLElement>>;

  @Input() public type: string = ''
  @Input() public rsvp: boolean;
  @Input() public appPreviewOverride: string;
  @Input() public isMobile: boolean = false;
  @Input() public templateHtml: SafeHtml = '';
  @Input() public rsvpHtml: SafeHtml = '';
  @Input() public noRsvpHtml: SafeHtml = '';
  @Input() public changeImageOptions: any = '';
  @Input() public selectedTabIndex: number = 0;
  @Input() public isChangeThemeEnabled: boolean = false;
  @Input() public isChangeImageEnabled: boolean = false;
  @Input() public isChangeCopyEnabled: boolean = false;
  @Input() public rewardLocationName: string = '';
  @Input() public rewardGiftMessage: string = '';
  @Input() public rewardProductName: string = '';
  @Input() public rewardProductImageUrl: string = '';
  @Input() public includeOperatorPhoto: boolean = true;
  @Input() public rewardOperatorImageUrl: string = '';
  @Input() public rewardOperatorName: string = '';
  @Input() public rewardExpiryDate: string = REWARD_APP_PREVIEW_EXPIRY_DISPLAY_FORMAT;

  @Output() public changeImageClicked = new EventEmitter();
  @Output() public changeThemeClicked = new EventEmitter();
  @Output() public changeCopyClicked = new EventEmitter();
  @Output() public selectedTabChanged = new EventEmitter();
  @Output() public backBtnClicked = new EventEmitter();

  public reward: string = EngageConstants.offer;
  public discoverReward: string = EngageConstants.discover;
  public email: string = EngageConstants.email;
  public event: string = EngageConstants.event;
  public bonusPoint: string = DiscoverConstants.bonusPoint;
  public cce: string = DiscoverConstants.categoryCommunityCare;
  public thirdPartPromo: string = DiscoverConstants.thirdPartyPromotion;
  public freeInCart: string = DiscoverConstants.freeItemInCartPromotion;

  private resizeObserver?: ResizeObserver;
  private previewShellsSub?: Subscription;
  private scaleRafId?: number;

  public ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.queueScaleRecalc());
    this.observePreviewShells();
    this.previewShellsSub = this.previewShells.changes.subscribe(() => {
      this.observePreviewShells();
      this.queueScaleRecalc();
    });
    this.queueScaleRecalc();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateHtml'] || changes['rsvpHtml'] || changes['noRsvpHtml'] || changes['selectedTabIndex']) {
      this.queueScaleRecalc();
    }
  }

  public ngOnDestroy(): void {
    if (this.scaleRafId) {
      cancelAnimationFrame(this.scaleRafId);
      this.scaleRafId = undefined;
    }
    this.previewShellsSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  public additionalImagesAvailable() {
    // Now that we have the ability to filter out image options based on theme we need to handle the case
    // when the selected theme has no images available even though the template itself does
    return this.changeImageOptions && this.changeImageOptions.length > 0
      ? this.changeImageOptions[0].changeImageOptions && this.changeImageOptions[0].changeImageOptions.length > 0
      : false;
  }

  public shouldShow(tabType: string): boolean {
    const type = this.type ?? '';
    const isBonusPointType = type === DiscoverConstants.bonusPoint || type === DiscoverConstants.promotion;
    const promoTypes = [DiscoverConstants.bonusPoint, DiscoverConstants.promotion, DiscoverConstants.categoryCommunityCare,
      DiscoverConstants.thirdPartyPromotion, DiscoverConstants.freeItemInCartPromotion]
    if (tabType == this.email) {
      return !promoTypes.includes(type);
    }
    if (tabType === this.event && type.toLowerCase() === this.event) {
      return this.rsvp;
    }
    if (tabType === this.bonusPoint) {
      return isBonusPointType;
    }
    if (tabType === this.reward) {
      let rewardTypes = [this.discoverReward, DiscoverConstants.reward, DiscoverConstants.automation,
        CampaignType.Reward, this.reward, CampaignType.Automation]
      return rewardTypes.includes(type);
    }
    return tabType === type
  }

  public onSelectedTabChange(index: number): void {
    this.selectedTabChanged.emit(index);
    this.queueScaleRecalc();
  }

  public getRewardOperatorImageUrl(): string {
    return this.rewardOperatorImageUrl || this.operatorPlaceholderImageUrl;
  }

  public handleOperatorImageError(): void {
    this.rewardOperatorImageUrl = this.operatorPlaceholderImageUrl;
  }

  private observePreviewShells(): void {
    if (!this.resizeObserver || !this.previewShells) {
      return;
    }

    this.resizeObserver.disconnect();
    this.previewShells.forEach((shellRef) => this.resizeObserver?.observe(shellRef.nativeElement));
  }

  private queueScaleRecalc(): void {
    if (this.scaleRafId) {
      cancelAnimationFrame(this.scaleRafId);
    }

    this.scaleRafId = requestAnimationFrame(() => this.updatePreviewScales());
  }

  private updatePreviewScales(): void {
    if (!this.previewShells || !this.previewStages) {
      return;
    }

    const shells = this.previewShells.toArray();
    const stages = this.previewStages.toArray();

    shells.forEach((shellRef, index) => {
      const shell = shellRef.nativeElement;
      const stage = stages[index]?.nativeElement;

      if (!stage) {
        return;
      }

      stage.style.transform = 'scale(1)';
      const contentWidth = stage.scrollWidth;
      const contentHeight = stage.scrollHeight;
      const availableWidth = shell.clientWidth;

      if (!contentWidth || !availableWidth) {
        shell.style.height = '';
        return;
      }

      const scale = Math.min(1, availableWidth / contentWidth);
      stage.style.transform = `scale(${scale})`;
      shell.style.height = `${contentHeight * scale}px`;
    });
  }
}
